import {createTheme} from "@mui/material/styles";
import {Plugin, PluginKey} from "@tiptap/pm/state";
import {ReactRenderer} from "@tiptap/react";
import Suggestion, {exitSuggestion, SuggestionPluginKey, type SuggestionOptions, type SuggestionProps} from "@tiptap/suggestion";
import type {ComponentType} from "react";
import type {PickerItem} from "../picker/pickerItem";
import {
    VariablePickerPanel,
    type VariablePickerHandle,
    type VariablePickerPanelProps,
} from "../picker/VariablePickerPanel";
import {
    clearTriggerForPick,
    finishVariablePickerTrigger,
    VARIABLE_PICKER_SUGGESTION_KEY,
    type VariablePickerTrigger,
    variablePickerTriggerState,
} from "./openVariablePicker";

/*
    The suggestion plugin mounts the popup into the body before React renders anything into it, so
    the layer has to be named here rather than read from a theme hook. One under a modal is where
    the link popover and the automations' slash menu already sit; without it the popup stacks by
    document order and reads as see-through against whatever it covers.
 */
const PAGE_LAYER = createTheme().zIndex.modal - 1;

/**
 * Above the dialog the editor sits in, else one under a modal. Read from the DOM, so it follows the
 * app's theme. The bubble menu stacks the same way.
 */
export const mountLayer = (editorDom: Element) => {
    const modal = editorDom.closest(".MuiModal-root");
    if (!(modal instanceof HTMLElement)) return String(PAGE_LAYER);

    const layer = Number.parseInt(window.getComputedStyle(modal).zIndex, 10);
    return String((Number.isFinite(layer) ? layer : PAGE_LAYER + 1) + 1);
};

const isEscape = (event: KeyboardEvent) => event.key === "Escape" || event.key === "Esc";

/**
 * The popup's lifecycle for any `/` suggestion: the generic variable one, or a host's own trigger
 * that keeps its node and only wants this popup. Returns the escape-first plugin, then the suggestion.
 */
export function variablePickerPlugins<I extends PickerItem>(
    suggestion: Omit<SuggestionOptions<I, I>, "render">,
    view: {search?: boolean; emptyLabel?: string} = {},
): Plugin[] {
    const pluginKey = suggestion.pluginKey ?? SuggestionPluginKey;
    const triggerKey = new PluginKey<VariablePickerTrigger | null>("variablePickerTrigger");
    // The panel is generic over its items; this suggestion only ever hands it `I`.
    const Panel = VariablePickerPanel as unknown as ComponentType<VariablePickerPanelProps<I>>;

    let renderer: ReactRenderer<VariablePickerHandle, VariablePickerPanelProps<I>> | null = null;
    let unmount: (() => void) | null = null;
    let selected = false;

    return [
        /*
            The suggestion plugin answers every Escape by exiting, before it ever learns what the
            popup made of the key. The popup's own layers get first refusal here: while the info
            card is open, Escape closes the card and stops there. While the popup is open, Escape
            is its own either way: a host Dialog ignores `defaultPrevented` and would close with it.

            Its state remembers the `/` that `openVariablePicker` typed, for `onExit` to take out.
         */
        new Plugin({
            key: triggerKey,
            [VARIABLE_PICKER_SUGGESTION_KEY]: pluginKey,
            state: variablePickerTriggerState(pluginKey),
            props: {
                handleKeyDown: (editorView, event) => {
                    if (!isEscape(event)) return false;

                    const handled = renderer?.ref?.handleKey(event) ?? false;
                    const active = (pluginKey.getState(editorView.state) as {active?: boolean} | undefined)?.active;
                    if (handled || active) event.stopPropagation();

                    return handled;
                },
            },
        }),

        Suggestion<I, I>({
            ...suggestion,
            pluginKey,

            render: () => {
                const panelProps = (props: SuggestionProps<I, I>): VariablePickerPanelProps<I> => ({
                    items: props.items,
                    query: props.query,
                    search: view.search,
                    emptyLabel: view.emptyLabel,
                    // The command carries the range the query occupies, so it is taken fresh each time.
                    onSelect: (item: I) => {
                        selected = true;

                        const trigger = triggerKey.getState(props.editor.state);
                        if (!trigger || trigger.slash !== props.range.from) {
                            props.command(item);
                            return;
                        }

                        // The toolbar's `/` is not in history, so the pick goes in beside it, not over it.
                        const range = clearTriggerForPick(props.editor, trigger, props.range.to);
                        suggestion.command?.({editor: props.editor, range, props: item});
                    },
                    onClose: () => {
                        exitSuggestion(props.editor.view, pluginKey);
                        props.editor.commands.focus();
                    },
                });

                return {
                    onStart: props => {
                        selected = false;
                        renderer = new ReactRenderer<VariablePickerHandle, VariablePickerPanelProps<I>>(Panel, {
                            editor: props.editor,
                            props: panelProps(props),
                        });
                        renderer.element.style.zIndex = mountLayer(props.editor.view.dom);
                        unmount = props.mount(renderer.element);
                    },

                    onUpdate: props => renderer?.updateProps(panelProps(props)),

                    onKeyDown: ({event}) => {
                        const handled = renderer?.ref?.handleKey(event) ?? false;
                        // A key the picker took belongs to it, not to whatever surface contains the editor.
                        if (handled) event.stopPropagation();

                        return handled;
                    },

                    onExit: props => {
                        unmount?.();
                        renderer?.destroy();
                        unmount = null;
                        renderer = null;

                        finishVariablePickerTrigger(props.editor, triggerKey, selected);
                        selected = false;
                    },
                };
            },
        }),
    ];
}

export default variablePickerPlugins;
